import { readApiErrorCode } from "@/utils/lifecycleErrors";

import { CustomEvent } from "../schema/customEvent";
import { ScrapeRequestStatus } from "../schema/scrapeRequest";

/**
 * Why a delivery was turned away, and whether one can succeed at all (PREC-3974).
 *
 * The admin delivery cell used to report nothing: "Mark delivered" fired its mutation without
 * awaiting it, and the deliver dialog printed one sentence about invalid XLSX for every refusal
 * the server could produce. Both failure modes looked exactly like a dead button, which is how
 * PREC-3974 was reported. These helpers give each refusal its own sentence, and let the cell tell
 * in advance that the escape hatch cannot work yet.
 */

/** The i18n keys under `customEvent.rescrape.admin.errors`, one per refusal the API can return. */
const ERROR_KEY_BY_CODE: Record<string, string> = {
	NO_UNCLAIMED_FILE: "noUnclaimedFile",
	SCRAPE_REQUEST_NOT_OPEN: "requestNotOpen",
	EMPTY_FILE: "emptyFile",
	LIST_NOT_FOUND: "listNotFound",
	NO_FILE_PROVIDED: "noFileProvided",
	NOT_FOUND: "notFound",
	FORBIDDEN: "forbidden",
};

/** Shown for a refusal we have no sentence for — a 500, a proxy error, a lost connection. */
export const GENERIC_DELIVERY_ERROR_KEY = "generic";

/**
 * The `customEvent.rescrape.admin.errors.*` key describing a rejected delivery request.
 *
 * Reuses `readApiErrorCode`, which already knows that Nest puts the machine code in `message` on
 * our own exceptions and a human string in `error` on its defaults.
 */
export const deliveryErrorKey = (error: unknown): string =>
	ERROR_KEY_BY_CODE[readApiErrorCode(error) ?? ""] ?? GENERIC_DELIVERY_ERROR_KEY;

/**
 * The newest file of a source that no delivered request already points at, mirroring the API's
 * `findUnclaimedFile`.
 *
 * The row payload carries both `csvEnrichments` and `scrapeRequests`, so the cell can apply the
 * server's own rule before offering the button. That matters more than it sounds: almost every
 * open request in the queue has no file yet, so without this the escape hatch is offered on rows
 * where it can only ever be refused.
 */
export const unclaimedFileId = (customEvent: CustomEvent): number | undefined => {
	const claimed = new Set(
		(customEvent.scrapeRequests || [])
			.filter((request) => request.status === ScrapeRequestStatus.DELIVERED)
			.map((request) => request.csvEnrichmentId)
			.filter((id): id is number => !!id)
	);

	return [...(customEvent.csvEnrichments || [])]
		.map((file) => file.id)
		.sort((a, b) => b - a)
		.find((id) => !claimed.has(id));
};
